import React from 'react';
import { Box } from '@mui/material';
import { usePlatformFlags } from '@askporter/api-client';
import { useTranslation } from '@askporter/config-provider';
import { AlertBox } from '../AlertBox';
import { AlertBoxMessage } from '../AlertBox/AlertBoxMessage';
import { AlertBoxTitle } from '../AlertBox/AlertBoxTitle';

/** Key of the global release flag (henry-rollins) that drives this banner. */
export const DEGRADED_SERVICE_FLAG = 'degraded-service';

export interface DegradedServiceBannerProps {
  /** Optional margin applied to the underlying AlertBox. */
  margin?: string;
}

/**
 * Warning banner shown while the global `degraded-service` release flag is on.
 * The flag is served by henry-rollins via Grieg's `/api/v1/static/flags`
 * endpoint and polled by {@link usePlatformFlags}, so the banner appears and
 * clears within roughly a minute of the flag being flipped.
 *
 * Renders nothing when the flag is off or absent. Text comes from the
 * `ns.degraded_service:*` translation keys, with English fallbacks so it works
 * before those strings are published.
 */
export const DegradedServiceBanner: React.FC<DegradedServiceBannerProps> = ({ margin = '8px 8px 0' }) => {
  const flags = usePlatformFlags();
  const { t } = useTranslation();

  if (!flags[DEGRADED_SERVICE_FLAG]) return null;

  const title = t('ns.degraded_service:title') || 'Some services are degraded';
  const message =
    t('ns.degraded_service:message') ||
    "We're experiencing technical issues that may cause delays. We're working to restore full service.";

  return (
    <Box data-testid="degraded-service-banner">
      <AlertBox alertType="warning" alertBoxMargin={margin}>
        <AlertBoxTitle disableMarginTop>{title}</AlertBoxTitle>
        <AlertBoxMessage>{message}</AlertBoxMessage>
      </AlertBox>
    </Box>
  );
};
